import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Form, Input, Button } from '../../core/styles/styledComponents';
import styled from 'styled-components';
import { theme } from '../../core/styles/theme';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../core/hooks/useToast';
import { validateResetToken, resetPassword } from '../../services/authService';

const Logo = styled.img`
  height: 50px;
  margin: ${theme.spacing.lg} 0;
`;

const ResetPasswordContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.background.default};
`;

const ResetPasswordCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  margin-top: ${theme.spacing.lg};
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${theme.spacing.md};
`;

const PasswordInput = styled(Input)`
  padding-right: 100px;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  font-size: ${theme.typography.body.fontSize};
  padding: 5px;
  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.danger.main};
  font-size: ${theme.typography.body.fontSize};
  margin-top: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
`;

const LoadingMessage = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.fontSize};
  margin-top: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
`;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { isLoading } = useAuth();
  const { error, success } = useToast();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        error('Invalid or missing reset token');
        navigate('/forgot-password');
        return;
      }

      try {
        const response = await validateResetToken(token);
        setIsTokenValid(response.valid);
        if (!response.valid) {
          error('Token expired or invalid.');
          setTimeout(() => navigate('/forgot-password'), 2000);
        }
      } catch (err) {
        error('Failed to validate reset token');
        setTimeout(() => navigate('/forgot-password'), 2000);
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token, navigate, error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!newPassword || !confirmPassword) {
      setFormError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }

    if (!token) {
      setFormError('Reset token is missing');
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      success('Password has been reset successfully');
      navigate('/login');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setFormError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidatingToken) {
    return (
      <ResetPasswordContainer>
        <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
        <ResetPasswordCard>
          <LoadingMessage>Validating reset token...</LoadingMessage>
        </ResetPasswordCard>
      </ResetPasswordContainer>
    );
  }

  if (!isTokenValid) {
    return (
      <ResetPasswordContainer>
        <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
        <ResetPasswordCard>
          <h2 style={{ marginBottom: theme.spacing.lg }}>Invalid Token</h2>
          <p style={{ marginBottom: theme.spacing.lg, textAlign: 'center' }}>
            Token expired or invalid.
          </p>
          <Button type="button" variant="primary" onClick={() => navigate('/forgot-password')}>
            Request New Reset Link
          </Button>
        </ResetPasswordCard>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer>
      <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
      <ResetPasswordCard>
        <h2 style={{ marginBottom: theme.spacing.lg }}>Reset Password</h2>
        <p style={{ marginBottom: theme.spacing.lg, textAlign: 'center' }}>
          Please enter your new password.
        </p>
        <Form onSubmit={handleSubmit}>
          <PasswordContainer>
            <PasswordInput
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <TogglePasswordButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </TogglePasswordButton>
          </PasswordContainer>
          <PasswordContainer>
            <PasswordInput
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <TogglePasswordButton
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </TogglePasswordButton>
          </PasswordContainer>
          {formError && <ErrorMessage>{formError}</ErrorMessage>}
          <Button type="submit" variant="primary" disabled={isSubmitting || isLoading}>
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </Button>
          <Button type="button" variant="minimal" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Form>
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};

export default ResetPasswordPage; 